/**
 * A sheet's coalesced recompute of its computed rows: the rows waiting for a
 * recompute (each over the union of the ranges asked for it) and the one
 * drain that runs them, and the tally behind the drain's timing span.
 *
 * Multiple `ColumnDataChanged` events on siblings of the same computed
 * parent collapse to a single `recompute(unionRange)`. The drain loop
 * re-enters its own queue when a parent's recompute itself emits more
 * `ColumnDataChanged` (cascading up another `ComputedDataSource` ancestor),
 * so each ancestor runs at most once per outermost dispatch.
 *
 * A computed row waits while a row it reads (a tree child, or a row it
 * depends on) has an install the frame budget carried (`Host.carried`): each
 * landing enqueues it again, and the drain after the last one recomputes it
 * once. Run earlier, its recompute would read those rows and force every
 * carried install in one turn, the read of one row paying for all its
 * siblings (sdk-kickoff 1). A read of a computed row lands its inputs'
 * carried installs together and recomputes it first (`finish`). The batch
 * install lands every input of one reader on the same turn (`readers`), so
 * the reader merges once a batch, not after a turn per input (sdk-kickoff 2).
 *
 * **Package-internal**: `Sheet` owns one. Which row is computed, its tree
 * parent, its dependents and its recompute come through
 * `SheetRecomputeQueue.Host`; it names no row type (the sheet instantiates
 * `Row`), so it sits outside the rows' type knot.
 */

import type { Option } from "../core/option.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { NaiveDate } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";

/** See the module note: a sheet's computed-row recompute queue, over its `Host`. */
export class SheetRecomputeQueue<Row> {
  readonly #host: SheetRecomputeQueue.Host<Row>;
  readonly #scheduler: RecomputeScheduler;
  readonly #pending = new Map<RowLocalId, NaiveDate.Range>();
  #draining = false;
  /** A read is finishing a carried row (`finish`): a drain its recompute triggers waits for the scheduled tick. */
  #finishing = false;
  #scheduled = false;
  #retired = false;
  /** Queued rows a drain left waiting for their inputs' carried installs (a read of one recomputes it: `finish`). */
  readonly #waited = new Set<RowLocalId>();

  /** A queue over `host`; `scheduler` decides how much one drain runs
   *  (default `SheetRecomputeQueue.IMMEDIATE`: everything, now). */
  constructor(
    host: SheetRecomputeQueue.Host<Row>,
    scheduler: RecomputeScheduler = SheetRecomputeQueue.IMMEDIATE,
  ) {
    this.#host = host;
    this.#scheduler = scheduler;
  }

  /** Enqueue a computed row (by id) for recompute over `range`,
   *  union-merging the range if it is already pending. Drained by
   *  `drain`. Shared by the parent-aggregate path
   *  (`enqueueParent`) and the cross-row DataRef path
   *  (`enqueueDependents`). */
  #enqueue(rowId: RowLocalId, range: NaiveDate.Range): void {
    if (this.#retired) return;
    const existing = this.#pending.get(rowId);
    if (existing == null) {
      this.#pending.set(rowId, range);
      return;
    }
    const startDse = Math.min(existing.start.dse, range.start.dse);
    const endDse = Math.max(existing.end.dse, range.end.dse);
    const start = startDse === existing.start.dse ? existing.start : range.start;
    const end = endDse === existing.end.dse ? existing.end : range.end;
    this.#pending.set(rowId, new NaiveDate.Range(start, end));
  }

  /** Enqueue `childId`'s tree parent over `range` when it is a computed row. */
  enqueueParent(
    childId: RowLocalId,
    range: NaiveDate.Range,
  ): void {
    const parentId = this.#host.parent(childId);
    if (parentId == null) return;
    if (this.#host.computed(parentId) == null) return;
    this.#enqueue(parentId, range);
  }

  /**
   * Recompute cross-row computed dependents of the changed row.
   * A `DerivedDataSource` that reads `changedId` via a `source` DataRef is NOT
   * its tree parent, so `enqueueParent` never reaches it. Walk the
   * dependency graph (Derived→Source edges registered by
   * `DerivedDataSource.registerDependencyEdges`) and enqueue each computed
   * dependent onto the same drain. This is what makes a derived row re-derive
   * when its source's cells change — including async server-fetch arrival, since
   * the sheet's canonical-cell fan-out re-emits `ColumnDataChanged` for the source
   * row. (Formula dependents are handled separately by
   * `FormulaEngine.recomputeDependents`.)
   */
  enqueueDependents(
    changedId: RowLocalId,
    range: NaiveDate.Range,
  ): void {
    for (const depId of this.#host.dependents(changedId)) {
      if (depId === changedId) continue;
      if (this.#host.computed(depId) != null) this.#enqueue(depId, range);
    }
  }

  /** Recompute queued rows, re-entering for rows a recompute queues; a nested call is a no-op.
   *  Runs at least one row, then more while the scheduler's `remainingMs()` is
   *  above zero; rows left over drain on the tick the scheduler runs next. */
  drain(): void {
    this.#drain(false);
  }

  /** Recompute every queued row now, whatever the scheduler's budget (the
   *  sheet's `dispose`, before `retire`: every carried row finishes while its
   *  stores are live). */
  flush(): void {
    this.#drain(true);
  }

  /** Recompute row `rowId` now when it is queued and a scheduled tick is
   *  outstanding, or a drain left it waiting for its inputs' carried installs
   *  (a read of a carried row finishes that row first, so no reader sees its
   *  pre-edit cells). A computed row's inputs whose installs the budget
   *  carried install first, together (`Host.install`), so the read sees
   *  their answers. Exactly that row: a drain its recompute
   *  triggers (the rows it queues, the other carried rows) waits for the
   *  outstanding tick, so a read never spends more than its own row's
   *  recompute. Otherwise nothing: no tick outstanding (the immediate
   *  default drains in the edit's own dispatch), the row not queued, or the
   *  queue retired. A read inside a recompute may finish the row it reads. */
  finish(rowId: RowLocalId): void {
    if (this.#retired) return;
    const inputs = this.#carriedInputsOf(rowId);
    if (inputs.length > 0) this.#host.install(inputs);
    if (!this.#scheduled && !this.#waited.has(rowId)) return;
    const range = this.#pending.get(rowId);
    if (range == null) return;
    this.#pending.delete(rowId);
    this.#waited.delete(rowId);
    const row = this.#host.computed(rowId);
    if (row == null) return;
    const outer = this.#finishing;
    this.#finishing = true;
    try {
      this.#host.recompute(row, range);
    } finally {
      this.#finishing = outer;
    }
  }

  /** The computed rows that read row `id`: its tree parent when computed, and
   *  its computed dependents. A batch install lands the rows sharing a reader
   *  on one turn (`SheetPrefetch`), so the reader merges once over them. */
  readers(id: RowLocalId): RowLocalId[] {
    const parent = this.#host.parent(id);
    const readers = parent != null && this.#host.computed(parent) != null ? [parent] : [];
    for (const dependent of this.#host.dependents(id)) {
      if (dependent !== id && this.#host.computed(dependent) != null) readers.push(dependent);
    }
    return readers;
  }

  /** Drop every queued row and turn every later enqueue, drain and scheduled
   *  tick into a no-op (the sheet's `dispose`, after its `flush`). */
  retire(): void {
    this.#retired = true;
    this.#pending.clear();
    this.#waited.clear();
  }

  #drain(all: boolean): void {
    if (this.#draining || this.#retired) return;
    // A read finishing its row: the tick `finish` requires is outstanding and drains the rest.
    if (this.#finishing && !all && this.#scheduled) return;
    this.#draining = true;
    const t0 = performance.now();
    const tally = RecomputeTally.empty();
    try {
      let ran = 0;
      const waits = all ? null : this.#waitingRows();
      for (;;) {
        const next = this.#nextRunnable(waits);
        if (next == null) break;
        if (!all && ran > 0 && this.#scheduler.remainingMs() <= 0) break;
        ran++;
        const [parentId, range] = next;
        this.#pending.delete(parentId);
        this.#waited.delete(parentId);
        const parent = this.#host.computed(parentId);
        if (parent == null) continue;
        tally.count(this.#host.recompute(parent, range));
      }
    } finally {
      this.#draining = false;
      // Rows waiting on carried installs need no tick: the turn that lands
      // their inputs drains them.
      if (!this.#scheduled && this.#nextRunnable(all ? null : this.#waitingRows()) != null) {
        this.#scheduled = true;
        this.#scheduler.schedule(() => {
          this.#scheduled = false;
          this.drain();
        });
      }
      // `sheet/recompute-drain` measures one aggregate-recompute drain: wall
      // time, rows recomputed, memo hits/misses. Emitted only past 50ms.
      tally.emit("sheet/recompute-drain", t0);
    }
  }

  /** The first queued row that may run now (`waits`: the rows that wait, which this marks; `null`: every row). */
  #nextRunnable(waits: Option<ReadonlySet<RowLocalId>>): Option<[RowLocalId, NaiveDate.Range]> {
    for (const entry of this.#pending) {
      if (waits == null || !waits.has(entry[0])) return entry;
      this.#waited.add(entry[0]);
    }
    return null;
  }

  /** The carried rows computed row `rowId` reads (a tree child, or a row it depends on); none when nothing is carried. */
  #carriedInputsOf(rowId: RowLocalId): RowLocalId[] {
    const inputs: RowLocalId[] = [];
    for (const id of this.#host.carried()) {
      if (this.#host.parent(id) === rowId || [...this.#host.dependents(id)].includes(rowId)) inputs.push(id);
    }
    return inputs;
  }

  /** The computed rows reading a row whose install the frame budget carried: its tree parent and its dependents. */
  #waitingRows(): ReadonlySet<RowLocalId> {
    const waits = new Set<RowLocalId>();
    for (const id of this.#host.carried()) {
      const parent = this.#host.parent(id);
      if (parent != null) waits.add(parent);
      for (const dependent of this.#host.dependents(id)) waits.add(dependent);
    }
    return waits;
  }
}

/**
 * When a computed-row recompute drain runs and how much of it: the port a host
 * injects to budget the recompute (the queue reads no clock and no frame itself).
 */
export interface RecomputeScheduler {
  /**
   * Run `drain` on a later tick; called once while rows are left over. The
   * host runs it even while it paints no frames (a hidden tab, a worker, a
   * headless host): a batch's carried installs and the computed rows waiting
   * on them move only on this drain, a read of a carried row or the sheet's
   * dispose, so a `schedule` that waits for a frame that never comes leaves
   * their loads pending (the browser host's `scheduleModelDrain` falls back
   * to a timer while its page is hidden).
   */
  schedule(drain: () => void): void;
  /** Milliseconds left in the current tick's budget; `<= 0` stops a drain after its first row. */
  remainingMs(): number;
}

/** Types that go with `SheetRecomputeQueue`: `Host`, `IMMEDIATE`. */
export namespace SheetRecomputeQueue {
  /** The default scheduler: every drain runs every queued row, synchronously. */
  export const IMMEDIATE: RecomputeScheduler = {
    schedule: (drain) => drain(),
    remainingMs: () => Infinity,
  };

  /** What the queue reads and does through its sheet. */
  export interface Host<Row> {
    /** Row `id` when it is a computed row of the sheet, else `null`. */
    computed(id: RowLocalId): Option<Row>;
    /** Row `id`'s tree parent (`null`: a root, or not in the sheet). */
    parent(id: RowLocalId): Option<RowLocalId>;
    /** The rows that depend on row `id` (the dependency graph's dependents). */
    dependents(id: RowLocalId): Iterable<RowLocalId>;
    /** The rows whose install the frame budget carried to a later turn (`SheetPrefetch.carriedRows`). */
    carried(): Iterable<RowLocalId>;
    /** Install rows `ids`' carried answers now, their listeners deferred to one drain (`SheetPrefetch.finishInstalls`). */
    install(ids: readonly RowLocalId[]): void;
    /** Recompute `row` over `range`; answers how its memo answered (`"hit"`, `"miss"`, …). */
    recompute(row: Row, range: NaiveDate.Range): Option<string>;
  }
}

/**
 * Per-pass tally behind the `sheet/recompute-drain` and
 * `sheet/recompute-sources` spans. The span is emitted after the pass rather
 * than wrapped around it, so breadcrumbs logged during the recompute stay root
 * entries instead of nesting under it.
 */
export class RecomputeTally {
  #rows = 0;
  #memoHits = 0;
  #memoMisses = 0;

  static empty(): RecomputeTally {
    return new RecomputeTally();
  }

  count(outcome: Option<string>): void {
    this.#rows += 1;
    if (outcome === "hit") this.#memoHits += 1;
    if (outcome === "miss") this.#memoMisses += 1;
  }

  /** Emit `name` when the pass since `t0` ran past the 50ms budget. */
  emit(name: string, t0: number): void {
    const ms = performance.now() - t0;
    if (ms <= 50) return;
    Tracing.span(name, (span) => {
      span.record("ms", Math.round(ms));
      span.record("rows", this.#rows);
      span.record("memoHits", this.#memoHits);
      span.record("memoMisses", this.#memoMisses);
    });
  }
}
